/**
 * Dispatch AthenaRuntimePlan to Node domain materializers.
 */

import type { AthenaClientConfig } from "../../client/contracts.ts";
import type { AthenaClientModelsInput } from "../../schema/types.ts";
import { materializeAuth } from "../materializers/auth.ts";
import {
  materializeBilling,
  type AthenaMaterializedBilling,
} from "../materializers/billing.ts";
import { materializeChat } from "../materializers/chat.ts";
import { materializeDatabase } from "../materializers/database.ts";
import { materializeStorage } from "../materializers/storage.ts";
import type { AthenaRuntimePlan } from "./types.ts";
import { validateRuntimePlan } from "./validate.ts";

export interface AthenaMaterializedRuntime {
  billing: AthenaMaterializedBilling;
  plan: AthenaRuntimePlan;
}

export interface AthenaRuntimeMaterialization<
  TModels extends AthenaClientModelsInput | undefined,
> {
  config: AthenaClientConfig<TModels>;
  runtime: AthenaMaterializedRuntime;
}

function materializeRuntime<
  TModels extends AthenaClientModelsInput | undefined,
>(
  plan: AthenaRuntimePlan,
  config: AthenaClientConfig<TModels>,
): AthenaRuntimeMaterialization<TModels> {
  // The plan owns routing and capability decisions in this slice. The
  // transformed config remains a resource/options handoff for injected
  // transports and domain-specific runtime settings; plan-owned materializer
  // slices are intentionally deferred rather than implied by this API.
  const withAuth = materializeAuth(config, plan);
  const withDb = materializeDatabase(withAuth, plan);
  const withStorage = materializeStorage(withDb, plan);
  const withChat = materializeChat(withStorage, plan);
  return {
    config: withChat,
    runtime: {
      billing: materializeBilling(withChat, plan),
      plan,
    },
  };
}

export function materializeRuntimePlan<
  TModels extends AthenaClientModelsInput | undefined,
>(
  plan: unknown,
  config: AthenaClientConfig<TModels>,
): AthenaMaterializedRuntime {
  const validated = validateRuntimePlan(plan);
  return materializeRuntime(validated, config).runtime;
}

export function materializeRuntimeWithConfig<
  TModels extends AthenaClientModelsInput | undefined,
>(
  plan: unknown,
  config: AthenaClientConfig<TModels>,
): AthenaRuntimeMaterialization<TModels> {
  const validated = validateRuntimePlan(plan);
  return materializeRuntime(validated, config);
}
